import {
  type AccordionContent,
  type AccordionItem,
  BLOCK_BACKGROUNDS,
  type BannerContent,
  type BlockBackground,
  type BlockButton,
  type ButtonVariant,
  type ButtonsContent,
  type CallToActionContent,
  type CardGridCard,
  type CardGridContent,
  type GalleryContent,
  type HeroContent,
  type IframeContent,
  type IconListContent,
  type PeopleContent,
  type SectionContent,
  type StepsContent,
  type StepsItem,
  type StatsContent,
  type TextContent,
  type VideoContent,
} from "./types";

type StoredContent = Record<string, unknown> | null;

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function isBlockBackground(value: unknown): value is BlockBackground {
  return BLOCK_BACKGROUNDS.includes(value as BlockBackground);
}

function background(value: unknown, fallback: BlockBackground = "default"): BlockBackground {
  return isBlockBackground(value) ? value : fallback;
}

function records(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Record<string, unknown> =>
      typeof item === "object" && item !== null && !Array.isArray(item),
  );
}

export function toCardGridContent(value: StoredContent): CardGridContent {
  const content = value ?? {};
  return {
    ...content,
    heading: text(content.heading),
    intro: text(content.intro),
    cards: records(content.cards) as CardGridCard[],
    background: background(content.background),
  };
}

export function toIframeContent(value: StoredContent): IframeContent {
  const content = value ?? {};
  return {
    ...content,
    heading: text(content.heading),
    intro: text(content.intro),
    src: text(content.src),
    title: text(content.title),
    background: background(content.background),
  };
}

export function toHeroContent(value: StoredContent): HeroContent {
  const content = value ?? {};
  return {
    ...content,
    title: text(content.title),
    imageUrl: text(content.imageUrl),
    imageAlt: text(content.imageAlt),
    links: records(content.links).map(link => ({
      ...link,
      label: text(link.label),
      href: text(link.href),
    })),
    background: background(content.background),
  };
}

export function toTextContent(value: StoredContent): TextContent {
  const content = value ?? {};
  return {
    ...content,
    heading: text(content.heading),
    body: text(content.body),
    background: background(content.background),
  };
}

export function toStatsContent(value: StoredContent): StatsContent {
  const content = value ?? {};
  return {
    ...content,
    heading: text(content.heading),
    stats: records(content.stats).map(stat => ({
      ...stat,
      value: text(stat.value),
      label: text(stat.label),
      tooltip: text(stat.tooltip),
    })),
    background: background(content.background),
  };
}

export function toPeopleContent(value: StoredContent): PeopleContent {
  const content = value ?? {};
  return {
    ...content,
    heading: text(content.heading),
    intro: text(content.intro),
    people: records(content.people).map(person => ({
      ...person,
      name: text(person.name),
      role: text(person.role),
      email: text(person.email),
      imageUrl: text(person.imageUrl),
    })),
    background: background(content.background),
  };
}

export function toIconListContent(value: StoredContent): IconListContent {
  const content = value ?? {};
  return {
    ...content,
    heading: text(content.heading),
    intro: text(content.intro),
    items: records(content.items).map(item => ({
      ...item,
      iconUrl: text(item.iconUrl),
      heading: text(item.heading),
      text: text(item.text),
    })),
    background: background(content.background),
  };
}

export function toButtonsContent(value: StoredContent): ButtonsContent {
  const content = value ?? {};
  return {
    ...content,
    buttons: records(content.buttons).map(button => ({
      ...button,
      label: text(button.label),
      href: text(button.href),
      variant: button.variant === "secondary" ? "secondary" : "primary",
    })),
    background: background(content.background),
  };
}

export function toGalleryContent(value: StoredContent): GalleryContent {
  const content = value ?? {};
  return {
    ...content,
    heading: text(content.heading),
    intro: text(content.intro),
    images: records(content.images).map(image => ({
      ...image,
      url: text(image.url),
      alt: text(image.alt),
      caption: text(image.caption),
    })),
    background: background(content.background),
  };
}

const LANDING_DEFAULT_BACKGROUNDS: Partial<Record<string, BlockBackground>> = {
  block_banner: "dark",
  block_call_to_action: "brand",
};

export function defaultBackground(blockType: string): BlockBackground {
  return LANDING_DEFAULT_BACKGROUNDS[blockType] ?? "default";
}

function optionalString(record: Record<string, unknown>, key: string): Record<string, string> {
  return typeof record[key] === "string" ? { [key]: record[key] } : {};
}

function readSection(record: Record<string, unknown>, blockType: string): SectionContent {
  return {
    ...optionalString(record, "heading"),
    background: background(record.background, defaultBackground(blockType)),
    ...optionalString(record, "anchorId"),
  };
}

function readNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function readItems<T extends { id: number; order: number }>(
  value: unknown,
  read: (item: Record<string, unknown>) => Omit<T, "id" | "order">,
): T[] {
  return records(value)
    .map(
      (item, index) =>
        ({
          ...item,
          ...read(item),
          id: readNumber(item.id, index + 1),
          order: readNumber(item.order, index),
        }) as T,
    )
    .sort((first, second) => first.order - second.order);
}

function readVariant(value: unknown, fallback: ButtonVariant): ButtonVariant {
  return value === "primary" || value === "secondary" ? value : fallback;
}

function readButton(value: unknown, variant: ButtonVariant): BlockButton | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return undefined;
  }
  const button = value as Record<string, unknown>;
  return {
    ...button,
    label: text(button.label),
    href: text(button.href),
    variant: readVariant(button.variant, variant),
  };
}

export function toBannerContent(value: StoredContent): BannerContent {
  const content = value ?? {};
  const primaryButton = readButton(content.primaryButton, "primary");
  const secondaryButton = readButton(content.secondaryButton, "secondary");
  return {
    ...content,
    ...readSection(content, "block_banner"),
    title: text(content.title),
    tagline: text(content.tagline),
    ...(primaryButton ? { primaryButton } : {}),
    ...(secondaryButton ? { secondaryButton } : {}),
    ...optionalString(content, "videoUrl"),
    ...optionalString(content, "poster"),
  };
}

export function toStepsContent(value: StoredContent): StepsContent {
  const content = value ?? {};
  return {
    ...content,
    ...readSection(content, "block_steps"),
    ...optionalString(content, "intro"),
    items: readItems<StepsItem>(content.items, item => ({
      ...optionalString(item, "heading"),
      text: text(item.text),
      ...optionalString(item, "image"),
      ...optionalString(item, "imageAlt"),
    })),
  };
}

export function toVideoContent(value: StoredContent): VideoContent {
  const content = value ?? {};
  return {
    ...content,
    ...readSection(content, "block_video"),
    ...optionalString(content, "intro"),
    url: text(content.url),
    title: text(content.title),
    ...optionalString(content, "caption"),
    ...optionalString(content, "poster"),
  };
}

export function toAccordionContent(value: StoredContent): AccordionContent {
  const content = value ?? {};
  return {
    ...content,
    ...readSection(content, "block_accordion"),
    items: readItems<AccordionItem>(content.items, item => ({
      question: text(item.question),
      answer: text(item.answer),
    })),
  };
}

export function toCallToActionContent(value: StoredContent): CallToActionContent {
  const content = value ?? {};
  return {
    ...content,
    ...readSection(content, "block_call_to_action"),
    ...optionalString(content, "text"),
    buttons: records(content.buttons)
      .map(button => readButton(button, "primary"))
      .filter((button): button is BlockButton => button !== undefined),
  };
}

export function nextItemId(items: Array<{ id: number }>): number {
  return items.length === 0 ? 1 : Math.max(...items.map(item => item.id)) + 1;
}

export function withoutEmpty<T extends object>(record: T, keys: string[]): T {
  const result = { ...record } as Record<string, unknown>;
  for (const key of keys) {
    if (typeof result[key] === "string") {
      const trimmed = (result[key] as string).trim();
      if (trimmed === "") {
        delete result[key];
      } else {
        result[key] = trimmed;
      }
    }
  }
  return result as T;
}

export function plainText(html: string): string {
  if (!html) return "";
  return (new DOMParser().parseFromString(html, "text/html").body.textContent ?? "").trim();
}

export function blankRichText(html: string): string {
  return plainText(html) === "" && !/<img|<iframe|<video/i.test(html) ? "" : html;
}
